import { useCallback, useRef, useState } from 'react'
import type { ConfirmDialogProps } from '@/components/ui/ConfirmDialog'
import type { TransferAddEmployeeModalCtx } from '../types'

type FlowConfirmPresentation = NonNullable<ConfirmDialogProps['presentation']>
type FlowConfirmMessageLayout = NonNullable<ConfirmDialogProps['messageLayout']>

export function useTransferProcessDialogs(processNotesIdParam: string, processIdParam: string) {
  const [process_id] = useState(processIdParam)
  const [process_id_notes, setProcessIdNotesState] = useState(() =>
    processIdParam && processNotesIdParam ? processNotesIdParam : '',
  )
  const [transferNotesRefreshKey, setTransferNotesRefreshKey] = useState(0)

  const bumpTransferLogNotesRefresh = useCallback(() => {
    setTransferNotesRefreshKey((key) => key + 1)
  }, [])

  /** Assign transfer log notes id and refresh the notes panel (Angular `noteListComponent.loadNotes`). */
  const setProcess_id_notes = useCallback(
    (id: string) => {
      setProcessIdNotesState(id)
      if (String(id).trim()) bumpTransferLogNotesRefresh()
    },
    [bumpTransferLogNotesRefresh],
  )

  const [confirmPaymentClone, setConfirmPaymentClone] = useState<{
    open: boolean
    companyPayload: Record<string, unknown> | null
  }>({ open: false, companyPayload: null })
  const [confirmInactiveListed, setConfirmInactiveListed] = useState(false)
  const [staffSelectionOpen, setStaffSelectionOpen] = useState(false)
  const [addEmployeeModalOpen, setAddEmployeeModalOpen] = useState(false)
  const [addEmployeeModalCtx, setAddEmployeeModalCtx] = useState<TransferAddEmployeeModalCtx | null>(
    null,
  )
  const [userEditModalUserId, setUserEditModalUserId] = useState<number | null>(null)

  const flowProceedRef = useRef<(() => void | Promise<void>) | null>(null)
  const flowCancelRef = useRef<(() => void | Promise<void>) | null>(null)
  const [flowConfirm, setFlowConfirm] = useState<{
    open: boolean
    title: string
    message: string
    primaryLabel: string
    secondaryLabel: string
    presentation: FlowConfirmPresentation
    primaryFirst: boolean
    messageLayout: FlowConfirmMessageLayout
  }>({
    open: false,
    title: 'Confirm',
    message: '',
    primaryLabel: 'Yes',
    secondaryLabel: 'No',
    presentation: 'saveChanges',
    primaryFirst: true,
    messageLayout: 'listingConfirm',
  })

  const resetFlowConfirmRefs = useCallback(() => {
    flowProceedRef.current = null
    flowCancelRef.current = null
  }, [])

  const openFlowConfirm = useCallback(
    (opts: {
      title?: string
      message: string
      primaryLabel?: string
      secondaryLabel?: string
      presentation?: FlowConfirmPresentation
      primaryFirst?: boolean
      messageLayout?: FlowConfirmMessageLayout
      onProceed: () => void | Promise<void>
      onCancel?: () => void | Promise<void>
    }) => {
      flowProceedRef.current = opts.onProceed
      flowCancelRef.current = opts.onCancel ?? null
      setFlowConfirm({
        open: true,
        title: opts.title ?? 'Confirm',
        message: opts.message,
        primaryLabel: opts.primaryLabel ?? 'Yes',
        secondaryLabel: opts.secondaryLabel ?? 'No',
        presentation: opts.presentation ?? 'saveChanges',
        primaryFirst: opts.primaryFirst ?? true,
        messageLayout: opts.messageLayout ?? 'listingConfirm',
      })
    },
    [],
  )

  const handleFlowConfirmPrimary = useCallback(() => {
    const fn = flowProceedRef.current
    resetFlowConfirmRefs()
    setFlowConfirm((state) => ({ ...state, open: false }))
    void fn?.()
  }, [resetFlowConfirmRefs])

  const handleFlowConfirmSecondary = useCallback(() => {
    const fn = flowCancelRef.current
    resetFlowConfirmRefs()
    setFlowConfirm((state) => ({ ...state, open: false }))
    void fn?.()
  }, [resetFlowConfirmRefs])

  const closeAddEmployeeModal = useCallback(() => {
    setAddEmployeeModalOpen(false)
    setAddEmployeeModalCtx(null)
  }, [])

  return {
    process_id,
    process_id_notes,
    setProcess_id_notes,
    transferNotesRefreshKey,
    bumpTransferLogNotesRefresh,
    confirmPaymentClone,
    setConfirmPaymentClone,
    confirmInactiveListed,
    setConfirmInactiveListed,
    staffSelectionOpen,
    setStaffSelectionOpen,
    addEmployeeModalOpen,
    setAddEmployeeModalOpen,
    addEmployeeModalCtx,
    setAddEmployeeModalCtx,
    closeAddEmployeeModal,
    userEditModalUserId,
    setUserEditModalUserId,
    flowConfirm,
    openFlowConfirm,
    handleFlowConfirmPrimary,
    handleFlowConfirmSecondary,
  }
}

export type TransferProcessDialogState = ReturnType<typeof useTransferProcessDialogs>
